import { useData, useDataAuth } from "../../runtime/provider";
import { captureException } from "@utils/captureException";
import { trpc, RouterOutputs } from "@utils/trpc";
import { getQueryKey } from "@trpc/react-query";
import type { z } from "zod";
import { recommendedPlaylistsPageWireSchema } from "@libs/wire";
import { infiniteDataSchema } from "@utils/durableCache/schemas";
import { makeOnUserChange } from "@hooks/useOnUserChange";

// Clear + refetch ONLY on a real login/logout transition (see the factory's
// doc comment — the old inline useEffect refired on every screen mount).
const useOnPlaylistsUserChange = makeOnUserChange();

const RECOMMENDED_PLAYLISTS_INITIAL_LIMIT = 6;
export const RECOMMENDED_PLAYLISTS_PAGE_LIMIT = 3;

export const RECOMMENDED_PLAYLISTS_INPUTS = {
  initialLimit: RECOMMENDED_PLAYLISTS_INITIAL_LIMIT,
  limit: RECOMMENDED_PLAYLISTS_PAGE_LIMIT,
};

type InfinitePlaylistsQuery = ReturnType<typeof useRecommendedPlaylists>;
export type RecommendedPlaylistsData =
  InfinitePlaylistsQuery["data"]["pages"][number]["data"][number];

type RecommendedPlaylistsPage =
  RouterOutputs["catalog"]["playlists"]["listRecommendedPlaylistsPaginated"];

// One-way drift check: see useForYou for rationale.
type _RecommendedPlaylistsPageWireSchemaCheck =
  RecommendedPlaylistsPage extends z.infer<
    typeof recommendedPlaylistsPageWireSchema
  >
    ? true
    : never;
const _recommendedPlaylistsPageWireSchemaDriftCheck: _RecommendedPlaylistsPageWireSchemaCheck = true;
void _recommendedPlaylistsPageWireSchemaDriftCheck;

const recommendedPlaylistsInfiniteSchema = infiniteDataSchema(
  recommendedPlaylistsPageWireSchema,
  recommendedPlaylistsPageWireSchema.shape.endCursor,
);

/**
 * Get the list of recommended playlists for the current user
 */
export const useRecommendedPlaylists = (options?: any) => {
  const data = useData();
  const { firebaseUser } = useDataAuth();
  const queryKey = getQueryKey(
    trpc.catalog.playlists.listRecommendedPlaylistsPaginated,
    RECOMMENDED_PLAYLISTS_INPUTS,
    "infinite",
  );
  const [, infiniteQuery] =
    trpc.catalog.playlists.listRecommendedPlaylistsPaginated.useSuspenseInfiniteQuery(
      RECOMMENDED_PLAYLISTS_INPUTS,
      {
        getNextPageParam: (lastPage: { endCursor?: number }) =>
          lastPage.endCursor,
        staleTime: 60 * 60 * 1000, // 1 hour — recommendations refresh hourly
        // gcTime must outlive staleTime (see useForYou, 2026-08-03).
        gcTime: 60 * 60 * 1000,
        ...data.cache.durableInfiniteOptions<RecommendedPlaylistsPage>(
          queryKey,
          recommendedPlaylistsInfiniteSchema,
        ),
        ...options,
      },
    );

  // Undefined until Firebase reports, null once it reports signed out.
  useOnPlaylistsUserChange(firebaseUser && firebaseUser.uid, () => {
    data.utils.catalog.playlists.listRecommendedPlaylistsPaginated.setInfiniteData(
      RECOMMENDED_PLAYLISTS_INPUTS,
      () => undefined,
    );
    data.utils.catalog.playlists.listRecommendedPlaylistsPaginated
      .refetch()
      .catch((error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "query",
          operation: "refetch-recommended-playlists-after-user-change",
        });
      });
  });

  return infiniteQuery;
};
